(() => {
  const CONFIG_ID = 'core-bit-selector-config';
  const REGISTERED_KEY = '__coreBitSelectorRegistered';
  const EPSILON = 0.001;

  function readConfig() {
    const node = document.getElementById(CONFIG_ID);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('Core-bit selector configuration is invalid.', error);
      return null;
    }
  }

  function parseDiameter(value) {
    if (typeof value === 'number') return value;
    if (typeof value !== 'string') return Number.NaN;

    const cleaned = value.trim().replace(/[\u2013\u2014]/g, '-').replace(/\"/g, '');
    const mixed = cleaned.match(/^(\d+)-(\d+)\/(\d+)/);
    if (mixed) return Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);

    const fraction = cleaned.match(/^(\d+)\/(\d+)/);
    if (fraction) return Number(fraction[1]) / Number(fraction[2]);

    const decimal = cleaned.match(/^\d+(?:\.\d+)?/);
    return decimal ? Number(decimal[0]) : Number.NaN;
  }

  function hasDiameter(product, diameter) {
    return product.diameters.some((candidate) => Math.abs(candidate - diameter) < EPSILON);
  }

  function mountFor(product, diameter) {
    const rule = product.mount_by_diameter?.find((candidate) => {
      const aboveMinimum = candidate.min == null || diameter >= candidate.min - EPSILON;
      const belowMaximum = candidate.max == null || diameter <= candidate.max + EPSILON;
      return aboveMinimum && belowMaximum;
    });
    return rule?.mount || product.mount;
  }

  function variantMatchesDiameter(variant, diameter) {
    const values = Array.isArray(variant.options) ? variant.options : [variant.title];
    return values.some((value) => Math.abs(parseDiameter(value) - diameter) < EPSILON);
  }

  function money(cents, currency) {
    if (!Number.isFinite(cents)) return null;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  }

  async function readLiveProduct(product, diameter, config, signal) {
    const url = new URL(`/products/${product.handle}.js`, window.location.origin);
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal,
    });

    if (!response.ok) throw new Error(`Product lookup returned HTTP ${response.status}.`);

    const payload = await response.json();
    const variants = (payload.variants || [])
      .filter((variant) => variantMatchesDiameter(variant, diameter))
      .map((variant) => ({
        title: variant.title,
        available: Boolean(variant.available),
        price: money(variant.price, config.currency || 'USD'),
        variant_id: String(variant.id),
        product_url: `${url.origin}/products/${product.handle}?variant=${variant.id}`,
      }));

    return {
      product: payload.title || product.label,
      handle: product.handle,
      product_url: `${url.origin}/products/${product.handle}`,
      mount: mountFor(product, diameter),
      drilling_methods: product.drilling_methods,
      materials: product.materials,
      bond_options: product.bond_options || [],
      variants,
      note: product.note || null,
    };
  }

  async function selectCoreBit(input, context, config) {
    const diameter = Number(input?.hole_diameter_in);
    const material = input?.material || 'unknown';
    const drillingMethod = input?.drilling_method || 'unknown';
    const mountThread = input?.mount_thread || 'unknown';
    const signal = context?.signal;
    const warnings = [];

    if (!Number.isFinite(diameter) || diameter <= 0) {
      return JSON.stringify({
        status: 'invalid_input',
        read_only: true,
        message: 'hole_diameter_in must be a positive number measured in inches.',
      });
    }

    if (material === 'unknown') warnings.push('Material was not confirmed. Verify the application before purchasing.');
    if (drillingMethod === 'unknown') warnings.push('Drilling method was not confirmed. Verify whether the job will be wet or dry.');
    if (mountThread === 'unknown') warnings.push('Mount thread was not confirmed. Verify the rig or adapter before purchasing.');

    const diameterMatches = config.products.filter((product) => hasDiameter(product, diameter));
    const compatible = diameterMatches.filter((product) => {
      const materialMatches = material === 'unknown' || product.materials.includes(material);
      const methodMatches = drillingMethod === 'unknown' || product.drilling_methods.includes(drillingMethod);
      const requiredMount = mountFor(product, diameter);
      const mountMatches = mountThread === 'unknown' || requiredMount === mountThread;
      return materialMatches && methodMatches && mountMatches;
    });

    if (!compatible.length) {
      const reasons = [];
      if (!diameterMatches.length) reasons.push(`No configured family has a ${diameter}-inch option.`);
      if (diameterMatches.length && drillingMethod !== 'unknown') reasons.push(`No ${drillingMethod} option matched.`);
      if (diameterMatches.length && material !== 'unknown') reasons.push(`No option verified for ${material.replaceAll('_', ' ')} matched.`);
      if (diameterMatches.length && mountThread !== 'unknown') reasons.push(`No ${mountThread} mount matched.`);

      return JSON.stringify({
        status: 'no_verified_match',
        read_only: true,
        hole_diameter_in: diameter,
        reasons,
        next_step: `Confirm the application with ${config.brand}: ${new URL(config.contact_url, window.location.origin).href}`,
      });
    }

    const candidates = await Promise.all(
      compatible.map(async (product) => {
        try {
          return await readLiveProduct(product, diameter, config, signal);
        } catch (error) {
          return {
            product: product.label,
            handle: product.handle,
            product_url: new URL(`/products/${product.handle}`, window.location.origin).href,
            mount: mountFor(product, diameter),
            drilling_methods: product.drilling_methods,
            materials: product.materials,
            bond_options: product.bond_options || [],
            variants: [],
            note: product.note || null,
            live_catalog_warning: error.message,
          };
        }
      }),
    );

    if (candidates.some((candidate) => candidate.bond_options.length > 1)) {
      warnings.push('More than one bond is offered. The selector does not choose a bond; confirm aggregate and reinforcement with the merchant.');
    }
    if (candidates.length > 1) {
      warnings.push('More than one verified product family matches. Compare construction and confirm the preferred family with the merchant.');
    }

    return JSON.stringify({
      status: warnings.length ? 'matches_need_confirmation' : 'verified_match',
      read_only: true,
      hole_diameter_in: diameter,
      requested: { material, drilling_method: drillingMethod, mount_thread: mountThread },
      candidates,
      warnings,
      next_step: 'Review live availability and the listed compatibility notes. This tool never adds items to a cart or starts checkout.',
      source: 'Merchant-configured compatibility rules plus a live, read-only Shopify product JSON lookup.',
    });
  }

  function toolDefinition(config) {
    return {
      name: config.tool_name || 'select_core_bit',
      description: `Read-only core-bit selector for ${config.brand}. Matches a requested finished hole diameter, material, drilling method, and mount thread against merchant-verified product rules, then reads current public Shopify variant availability and price. It never changes a cart, starts checkout, places an order, or processes payment.`,
      inputSchema: {
        type: 'object',
        properties: {
          hole_diameter_in: {
            type: 'number',
            exclusiveMinimum: 0,
            description: 'Required finished hole diameter in inches, such as 4 or 6.5.',
          },
          material: {
            type: 'string',
            enum: ['concrete', 'reinforced_concrete', 'masonry', 'brick', 'stone', 'unknown'],
            default: 'unknown',
            description: 'Material being drilled. Use unknown if it has not been confirmed.',
          },
          drilling_method: {
            type: 'string',
            enum: ['wet', 'dry', 'unknown'],
            default: 'unknown',
            description: 'Planned drilling method. Use unknown if it has not been confirmed.',
          },
          mount_thread: {
            type: 'string',
            enum: ['5/8-11', '1-1/4-7', 'unknown'],
            default: 'unknown',
            description: 'Core drill spindle or adapter thread. Use unknown if it has not been confirmed.',
          },
        },
        required: ['hole_diameter_in'],
        additionalProperties: false,
      },
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: true,
        untrustedContentHint: true,
      },
      execute: (input, context) => selectCoreBit(input, context, config),
    };
  }

  async function register() {
    if (window[REGISTERED_KEY]) return true;
    const config = readConfig();
    const modelContext = document.modelContext;
    if (!config || !modelContext?.registerTool) return false;

    try {
      await modelContext.registerTool(toolDefinition(config));
      window[REGISTERED_KEY] = true;
      return true;
    } catch (error) {
      console.warn('Core-bit selector could not be registered with WebMCP.', error);
      return false;
    }
  }

  window.CoreBitSelector = Object.freeze({
    parseDiameter,
    select: (input, context = {}) => {
      const config = readConfig();
      if (!config) return Promise.resolve(JSON.stringify({ status: 'unavailable', read_only: true }));
      return selectCoreBit(input, context, config);
    },
    register,
  });

  let attempts = 0;
  const timer = window.setInterval(async () => {
    attempts += 1;
    if ((await register()) || attempts >= 60) window.clearInterval(timer);
  }, 250);
})();
